import { useNavigate, useParams } from "react-router-dom";
import useContests from "../../../models/contests/useContests";
import { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import useRestaurants from "../../../models/restaurants/useRestaurants";
import FormTextInput from "../../../components/TextInput/Form";
import { yupResolver } from "@hookform/resolvers/yup";
import FormBasicSelect from "../../../components/BasicSelect/Form";
import { buildValidationSchema } from "./validations";
import { Button } from "@mui/material";
import { format } from "date-fns";
import ParticipantForm from "./ParticipantForm";
import Table from "../../../components/Table";
import DeleteIcon from "@mui/icons-material/Delete";
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { IconButton } from "@mui/material";
import { toast } from "react-toastify";
import useAuth from "../../../models/auth/useAuth";

import "./styles.scss";

const ContestDetail = () => {
    const { id } = useParams();
    const { contest, fetchContest, createContest, updateContest, deleteParticipant, fetchParticipants, clearContest } = useContests();
    const { restaurantList, fetchRestaurantList } = useRestaurants();
    const [participantFormOpen, setParticipantFormOpen] = useState(false);
    const navigate = useNavigate();
    const { user: { restaurant_worker } } = useAuth();
    const isAdmin = restaurant_worker && restaurant_worker.is_admin;

    const methods = useForm({
        mode: "onChange",
        resolver: yupResolver(buildValidationSchema()),
        defaultValues: {
            restaurant: contest?.restaurant || "",
            prize: contest?.prize || "",
            start_date: contest?.start_date ? format(contest?.start_date, "yyyy-MM-dd") : "",
            end_date: contest?.end_date ? format(contest?.end_date, "yyyy-MM-dd") : "",
            min_stories_required: contest?.min_stories_required || 10,
            is_active: contest?.is_active || true,
        },
    });

    const {
        control,
        handleSubmit,
        reset,
        watch,
        formState: { isValid, isDirty, isSubmitting, errors },
    } = methods;

    const maxStories = useMemo(() => {
        if (!contest.participants) return 0;
        return Math.max(...contest.participants.map(participant => participant.validated_stories_count));
    }, [contest]);

    const tableColumns = [
        { headerName: "Nombre", field: "name", flex: 6, sortable: false },
        {
            headerName: "Nº stories",
            field: "validated_stories_count",
            flex: 3,
            sortable: false,
            renderCell: ({ value, row }) => (
                <div className="ContestDetail__participants__stories">
                    {value}{value >= Math.max(maxStories, contest.min_stories_required) ? (
                        <EmojiEventsIcon />
                    ) : (
                        <></>
                    )}
                </div>
            )
        },
        {
            headerName: "",
            type: "actions",
            field: " ",
            sortable: false,
            renderCell: ({ row }) => isAdmin && (
                <IconButton onClick={() => handleDeleteParticipant(row.id)}>
                    <DeleteIcon />
                </IconButton>
            ),
        },
    ];

    const handleDeleteParticipant = async (id) => {
        try {
            await deleteParticipant(id);
            fetchParticipants(contest.id);
            toast.success("Participante eliminado correctamente");
        } catch (error) {
            toast.error("Error al eliminar el participante");
        }
    }

    const onSubmit = async (data) => {
        try {
            if (id) {
                await updateContest(id, { ...data, min_stories_required: parseInt(data.min_stories_required) });
                await fetchContest(id);
                toast.success("Concurso actualizado correctamente");
            } else {
                const response = await createContest({ ...data, min_stories_required: parseInt(data.min_stories_required) });
                toast.success("Concurso creado correctamente");
                navigate(`/admin/contests/${response.id}`);
            }
        } catch (error) {
            if (id) {
                toast.error("Error al actualizar el concurso");
            } else {
                toast.error("Error al crear el concurso");
            }
        }
    }

    useEffect(() => {
        if (id) {
            fetchContest(id);
        }
    }, [id]);

    useEffect(() => {
        if (contest.id) {
            reset({
                ...contest,
                start_date: format(new Date(contest?.start_date), "yyyy-MM-dd"),
                end_date: format(new Date(contest?.end_date), "yyyy-MM-dd")
            });
        }
    }, [contest]);

    useEffect(() => {
        fetchRestaurantList();
        return () => {
            clearContest();
        }
    }, []);

    return (
        <>
            <div className="ContestDetail">
                <section>
                    <h2>
                        {id ? "Detalle del Concurso" : "Crear Nuevo Concurso"}
                    </h2>
                    <div className="ContestDetail__form">
                        <FormProvider {...methods} autoComplete={false}>
                            <div className="ContestDetail__form__fields">
                                <FormBasicSelect control={control} name="restaurant" label="Restaurante" options={(restaurantList || []).map((restaurant) => ({
                                    label: restaurant.name,
                                    value: restaurant.id,
                                }))} disabled={!isAdmin} />
                                <FormTextInput control={control} name="prize" label="Premio" disabled={!isAdmin} />
                                <FormTextInput control={control} name="min_stories_required" label="Nº mínimo de stories" disabled={!isAdmin} type="number" min={0} />
                            </div>
                            <div className="ContestDetail__form__fields">
                                <FormTextInput control={control} name="start_date" label="Fecha de inicio" placeholder="" type="date" disabled={!isAdmin} />
                                <FormTextInput control={control} name="end_date" label="Fecha de fin" type="date" disabled={!isAdmin} />
                            </div>
                            {isAdmin && (
                                <Button
                                    variant="contained"
                                    onClick={handleSubmit(onSubmit)}
                                    disabled={
                                        !isValid || !isDirty || isSubmitting
                                    }
                                >
                                    Guardar
                                </Button>
                            )}
                        </FormProvider>
                    </div>
                </section>

                {(contest.id && id) && (
                    <section className="ContestDetail__participants">
                        <div className="ContestDetail__participants__header">
                            <h2>Participantes</h2>
                            {isAdmin && (
                                <Button
                                    variant="contained"
                                    onClick={() => setParticipantFormOpen(true)}
                                >
                                    Añadir Participante
                                </Button>
                            )}
                        </div>
                        <div>
                            {contest.participants?.length > 0 ? (
                                <Table key={contest.participants?.length} columns={tableColumns} data={contest.participants} hidePagination />
                            ) : (
                                <div className="ContestDetail__participants__empty">
                                    <p>No hay participantes</p>
                                </div>
                            )}
                        </div>
                    </section>
                )}
            </div>
            <ParticipantForm isOpen={participantFormOpen} onClose={() => setParticipantFormOpen(false)} contestId={id} />
        </>
    );
};

export default ContestDetail;
